/// <reference types="vite/client" />
import './menu.css';
import type { CharacterDef, MenuResult, OnlineMatchInfo, RaceResult, RaceResultEntry, TrackDef, VehicleKind } from '../core/types';
import { showControlSetup, showSettings, type SoundApi } from './controls';
import { portraitDataUrl } from '../render/portrait';

export interface MenuOptions {
  characters: CharacterDef[];
  tracks: TrackDef[];
  mode?: 1 | 2 | 4;
  /** telefon: csak egyjátékos mód */
  mobile?: boolean;
  /** hangbeállítások a főmenü beállításaihoz */
  sound?: SoundApi;
  /** online lobby megnyitása; null = a játékos visszalépett */
  online?: { open: () => Promise<OnlineMatchInfo | null> };
}

export interface MenuController {
  /** a START megnyomásáig vár; a választással tér vissza */
  show(): Promise<MenuResult>;
  hide(): void;
}

type Mode = 1 | 2 | 4;
type Stage = 'title' | 'mode' | 'pick' | 'track';

const MODES: readonly Mode[] = [1, 2, 4];
const AI_COUNT: Record<Mode, number> = { 1: 5, 2: 4, 4: 2 };
const MODE_LABEL: Record<Mode, string> = { 1: '1 JÁTÉKOS', 2: '2 JÁTÉKOS', 4: '4 JÁTÉKOS' };
const MODE_NOTE: Record<Mode, string> = { 1: '(szóló)', 2: '(osztott kép)', 4: '(2×2)' };
const MODE_HINT: Record<Mode, string> = {
  1: 'Egyedül a hatfős mezőnyben',
  2: 'Ketten, fél-fél képernyőn',
  4: 'Négyen, 2×2-es rácsban',
};
const STAGE_STEP: Record<Stage, string> = {
  title: '1 / 4 · CÍM',
  mode: '2 / 4 · MÓD',
  pick: '3 / 4 · KARAKTER',
  track: '4 / 4 · PÁLYA',
};

const STAT_ROWS: readonly { key: keyof CharacterDef['stats']; label: string }[] = [
  { key: 'topSpeed', label: 'sebesség' },
  { key: 'accel', label: 'gyorsulás' },
  { key: 'handling', label: 'kormányzás' },
  { key: 'grip', label: 'tapadás' },
  { key: 'weight', label: 'tömeg' },
];

const VEHICLE_LABEL: Record<VehicleKind, string> = {
  kart: 'gokart',
  scooter: 'e-roller',
  minivan: 'kisbusz',
  wagon: 'kombi',
  courier: 'futárrobogó',
  van: 'furgon',
  sedan: 'szedán',
  beetle: 'bogár',
  taxi: 'taxi',
  hatch: 'ferdehátú',
};

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

const make = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
};

const makeButton = (className: string, label?: string): HTMLButtonElement => {
  const node = make('button', className);
  node.type = 'button';
  if (label !== undefined) node.textContent = label;
  return node;
};

interface CardRefs {
  card: HTMLButtonElement;
  taken: HTMLElement;
  picked: HTMLElement;
}

class MenuControllerImpl implements MenuController {
  private readonly root: HTMLElement;
  private readonly options: MenuOptions;
  private readonly characters: CharacterDef[];
  private readonly el: HTMLDivElement;
  private readonly screens: Record<Stage, HTMLElement>;

  private backEl!: HTMLButtonElement;
  private stepEl!: HTMLSpanElement;
  private readonly modeEls: HTMLButtonElement[] = [];
  private readonly cardRefs: CardRefs[] = [];
  private randomEl!: HTMLButtonElement;
  private pickBadge!: HTMLElement;
  private pickTitle!: HTMLElement;
  private pickSub!: HTMLElement;
  private pipsEl!: HTMLElement;
  private trackNameEl!: HTMLElement;
  private trackSubEl!: HTMLElement;
  private trackMetaEl!: HTMLElement;
  private trackPicksEl!: HTMLElement;
  private trackArtEl!: HTMLCanvasElement;
  private trackChoices: HTMLButtonElement[] = [];
  private readonly tracks: TrackDef[];
  private readonly mobile: boolean;
  private trackId: string;

  private visible = false;
  private stage: Stage = 'title';
  private mode: Mode = 1;
  private modeCursor = 0;
  private practice = false;
  private setupOpen = false;
  private settingsOpen = false;
  private onlineOpen = false;
  private noAi = false;
  private battle = false;
  private night = false;
  private aiToggleEl: HTMLButtonElement | null = null;
  private battleToggleEl: HTMLButtonElement | null = null;
  private nightToggleEl: HTMLButtonElement | null = null;
  private picks: (string | null)[] = [];
  private pickerIndex = 0;
  private cardCursor = 0;
  private onRandom = false;
  private pending: Promise<MenuResult> | null = null;
  private resolvePending: ((value: MenuResult) => void) | null = null;

  private readonly keyHandler = (event: KeyboardEvent): void => this.onKeyDown(event);

  constructor(root: HTMLElement, options: MenuOptions) {
    this.root = root;
    this.options = options;
    this.tracks = options.tracks.length > 0 ? options.tracks : [];
    this.mobile = options.mobile === true;
    const urlTrack = new URLSearchParams(window.location.search).get('track');
    this.trackId = this.tracks.some((t) => t.id === urlTrack) ? (urlTrack as string) : (this.tracks[0]?.id ?? '');
    const selectable = options.characters.filter((c) => c.selectable);
    this.characters = (selectable.length > 0 ? selectable : options.characters).slice(0, 4);
    this.mode = options.mode ?? 1;
    this.modeCursor = Math.max(0, MODES.indexOf(this.mode));

    this.el = make('div', 'menu');
    this.el.dataset.mobile = this.mobile ? 'true' : 'false';
    this.el.tabIndex = -1;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-label', 'Csúcsforgalom Kupa menü');

    const panel = make('div', 'menu-panel');
    const top = make('div', 'menu-top');
    this.backEl = makeButton('menu-back', '← VISSZA');
    this.backEl.hidden = true;
    this.backEl.addEventListener('click', () => this.back());
    this.stepEl = make('span', 'menu-step');
    this.stepEl.setAttribute('aria-hidden', 'true');
    top.append(this.backEl, this.stepEl);

    this.screens = {
      title: this.buildTitle(),
      mode: this.buildMode(),
      pick: this.buildPick(),
      track: this.buildTrack(),
    };

    panel.append(top, this.screens.title, this.screens.mode, this.screens.pick, this.screens.track);
    this.el.append(panel);
  }

  show(): Promise<MenuResult> {
    if (this.pending) return this.pending;

    this.mode = this.options.mode ?? 1;
    if (this.mobile) this.mode = 1;
    this.modeCursor = Math.max(0, MODES.indexOf(this.mode));
    this.practice = false;
    this.noAi = false;
    this.battle = false;
    this.night = false;
    this.onlineOpen = false;
    this.picks = Array.from({ length: this.mode }, () => null);
    this.pickerIndex = 0;
    this.cardCursor = 0;
    this.onRandom = false;
    this.stage = 'title';
    this.visible = true;

    if (!this.el.isConnected) this.root.appendChild(this.el);
    window.addEventListener('keydown', this.keyHandler, true);
    this.render();
    this.el.focus({ preventScroll: true });

    const pending = new Promise<MenuResult>((resolve) => {
      this.resolvePending = resolve;
    });
    this.pending = pending;
    return pending;
  }

  hide(): void {
    this.visible = false;
    window.removeEventListener('keydown', this.keyHandler, true);
    if (this.el.isConnected) this.el.remove();
    this.el.blur();
  }

  // ---------- képépítés ----------

  private buildTitle(): HTMLElement {
    const section = make('section', 'scr scr-title');
    section.setAttribute('aria-label', 'Címképernyő');

    const kicker = make('span', 'title-kicker');
    kicker.textContent = 'CSÚCSFORGALOM KUPA';

    const logo = make('h1', 'logo');
    const logoTop = make('span', 'logo-top');
    logoTop.textContent = 'SOMA';
    const logoBottom = make('span', 'logo-bottom');
    logoBottom.textContent = 'KART';
    logo.append(logoTop, logoBottom);

    const subtitle = make('p', 'title-sub');
    subtitle.textContent = 'Városi kart verseny – 6 induló, 3 kör';

    const cta = makeButton('cta', 'INDÍTÁS');
    cta.addEventListener('click', () => this.toMode());

    const practiceBtn = makeButton('ghost-btn practice-btn', 'GYAKORLÁS');
    practiceBtn.addEventListener('click', () => this.startPractice());

    const onlineBtn = makeButton('ghost-btn online-btn', 'ONLINE');
    onlineBtn.addEventListener('click', () => void this.openOnline());

    const settingsBtn = makeButton('ghost-btn settings-btn', 'BEÁLLÍTÁSOK');
    settingsBtn.setAttribute('aria-label', 'Beállítások');
    settingsBtn.addEventListener('click', () => this.openSettings());

    const lights = make('div', 'traffic');
    lights.setAttribute('aria-hidden', 'true');
    for (const color of ['#ff5c4d', '#ffc857', '#7ed957']) {
      const light = make('span', 'traffic-light');
      light.style.background = color;
      lights.append(light);
    }

    const hint = make('p', 'title-hint');
    hint.textContent = this.mobile
      ? 'Mobilon 1 játékos helyben · ONLINE 1–4 ember · érintéses vezérlés'
      : '1–4 játékos helyben · online 4 ember + AI · S = beállítások';

    section.append(kicker, logo, subtitle, lights, cta, onlineBtn, practiceBtn, settingsBtn, hint);
    return section;
  }

  private buildMode(): HTMLElement {
    const section = make('section', 'scr scr-mode');
    section.setAttribute('aria-label', 'Módválasztó');

    const head = make('div', 'scr-head');
    const kicker = make('span', 'kicker');
    kicker.textContent = '2. LÉPÉS';
    const title = make('h2', 'scr-heading');
    title.textContent = 'HOGYAN VERSENYZÜNK?';
    const sub = make('p', 'scr-sub');
    sub.textContent = 'Válaszd ki, hányan ülnek kormány mögé. A mezőny mindig hat fős.';
    head.append(kicker, title, sub);

    const grid = make('div', 'mode-grid');
    MODES.forEach((mode, index) => {
      const card = makeButton('mode-card');
      card.dataset.mode = String(mode);
      card.setAttribute('aria-pressed', 'false');

      const players = make('span', 'mode-players');
      players.textContent = MODE_LABEL[mode];
      const note = make('span', 'mode-note');
      note.textContent = MODE_NOTE[mode];
      const desc = make('span', 'mode-desc');
      desc.textContent = MODE_HINT[mode];
      const ai = make('span', 'mode-ai');
      const total = make('span', 'mode-total');
      const locked = this.mobile && mode !== 1;
      if (locked) {
        card.disabled = true;
        card.classList.add('is-locked');
        ai.textContent = 'Mobilon nem elérhető';
        total.textContent = '1 játékos';
      } else {
        ai.textContent = `${AI_COUNT[mode]} AI ellenfél`;
        total.textContent = '6 induló';
      }
      card.append(players, note, desc, ai, total);

      card.addEventListener('click', () => {
        if (locked) return;
        this.modeCursor = index;
        this.chooseMode();
      });
      card.addEventListener('focus', () => {
        this.modeCursor = index;
        this.renderMode();
      });
      card.addEventListener('pointerenter', () => {
        this.modeCursor = index;
        this.renderMode();
      });

      this.modeEls.push(card);
      grid.append(card);
    });

    const hint = make('p', 'hint');
    hint.textContent = this.mobile
      ? 'Mobilon a helyi coop nem elérhető · az ONLINE a címképernyőn indul'
      : '← → választás · Enter mehet · Esc vissza';

    section.append(head, grid, hint);
    return section;
  }

  private buildPick(): HTMLElement {
    const section = make('section', 'scr scr-pick');
    section.setAttribute('aria-label', 'Karakterválasztó');

    const head = make('div', 'pick-head');
    const left = make('div', 'pick-head-left');
    this.pickBadge = make('span', 'pick-badge');
    this.pickTitle = make('h2', 'scr-heading pick-heading');
    this.pickTitle.textContent = 'VÁLASSZ KARAKTERT';
    this.pickSub = make('p', 'scr-sub');
    left.append(this.pickBadge, this.pickTitle, this.pickSub);

    this.pipsEl = make('div', 'pick-pips');
    this.pipsEl.setAttribute('aria-label', 'Játékosok sorrendje');
    head.append(left, this.pipsEl);

    const grid = make('div', 'card-grid');
    this.characters.forEach((character, index) => {
      const card = makeButton('char-card');
      card.style.setProperty('--char', character.colors.body);
      card.style.setProperty('--char-accent', character.colors.accent);
      card.setAttribute('aria-label', `${character.name}, ${character.role}`);

      const top = make('div', 'char-top');
      const avatar = make('span', 'char-avatar');
      const portrait = document.createElement('img');
      portrait.className = 'char-avatar-img';
      portrait.src = portraitDataUrl(character, 96);
      portrait.alt = '';
      avatar.appendChild(portrait);
      const titles = make('div', 'char-titles');
      const name = make('span', 'char-name');
      name.textContent = character.name;
      const role = make('span', 'char-role');
      role.textContent = character.role;
      titles.append(name, role);
      top.append(avatar, titles);

      const vehicle = make('div', 'char-vehicle');
      const vehicleLabel = make('span', 'char-vehicle-label');
      vehicleLabel.textContent = 'JÁRMŰ';
      const vehicleName = make('span', 'char-vehicle-name');
      vehicleName.textContent = `${character.vehicle.name} · ${VEHICLE_LABEL[character.vehicle.kind]}`;
      vehicle.append(vehicleLabel, vehicleName);

      const stats = make('div', 'char-stats');
      for (const row of STAT_ROWS) {
        const line = make('div', 'stat');
        const label = make('span', 'stat-label');
        label.textContent = row.label;
        const track = make('span', 'stat-track');
        const fill = make('span', 'stat-fill');
        fill.style.width = `${clamp((character.stats[row.key] / 10) * 100, 0, 100)}%`;
        track.append(fill);
        line.append(label, track);
        stats.append(line);
      }

      const ability = make('div', 'char-ability');
      const abilityHead = make('div', 'ability-head');
      const abilityName = make('span', 'ability-name');
      abilityName.textContent = character.ability.name;
      const abilityCd = make('span', 'ability-cd');
      abilityCd.textContent = `${character.ability.cooldown} mp`;
      abilityHead.append(abilityName, abilityCd);
      const abilityDesc = make('p', 'ability-desc');
      abilityDesc.textContent = character.ability.desc;
      ability.append(abilityHead, abilityDesc);

      const taken = make('span', 'char-taken');
      taken.textContent = 'MÁR VÁLASZTVA';
      taken.hidden = true;
      const picked = make('span', 'char-picked');
      picked.textContent = 'KIVÁLASZTVA';
      picked.hidden = true;

      card.append(top, vehicle, stats, ability, taken, picked);

      card.addEventListener('click', () => {
        this.cardCursor = index;
        this.onRandom = false;
        this.chooseCharacter(index);
      });
      card.addEventListener('focus', () => {
        if (this.takenByOthers(character.id)) return;
        this.cardCursor = index;
        this.onRandom = false;
        this.renderPick();
      });
      card.addEventListener('pointerenter', () => {
        if (this.takenByOthers(character.id)) return;
        this.cardCursor = index;
        this.onRandom = false;
        this.renderPick();
      });

      this.cardRefs.push({ card, taken, picked });
      grid.append(card);
    });

    this.randomEl = makeButton('random-card');
    this.randomEl.setAttribute('aria-label', 'Véletlen karakter választása');
    const randomIcon = make('span', 'random-icon');
    randomIcon.textContent = '?';
    const randomText = make('span', 'random-text');
    const randomTitle = make('strong', 'random-title');
    randomTitle.textContent = 'VÉLETLEN';
    const randomSub = make('small', 'random-sub');
    randomSub.textContent = 'sorsoljuk ki helyetted';
    randomText.append(randomTitle, randomSub);
    this.randomEl.append(randomIcon, randomText);
    this.randomEl.addEventListener('click', () => this.chooseRandom());
    this.randomEl.addEventListener('focus', () => {
      this.onRandom = true;
      this.renderPick();
    });
    this.randomEl.addEventListener('pointerenter', () => {
      this.onRandom = true;
      this.renderPick();
    });

    const hint = make('p', 'hint');
    hint.textContent = '← → lépkedés · ↓ a Véletlenhez · Enter választás · Esc vissza';

    section.append(head, grid, this.randomEl, hint);
    return section;
  }

  private buildTrack(): HTMLElement {
    const section = make('section', 'scr scr-track');
    section.setAttribute('aria-label', 'Pályakártya');

    const head = make('div', 'scr-head');
    const kicker = make('span', 'kicker');
    kicker.textContent = '4. LÉPÉS · MINDEN KÉSZ';
    const title = make('h2', 'scr-heading');
    title.textContent = 'IRÁNY A PÁLYA!';
    const sub = make('p', 'scr-sub');
    sub.textContent = 'Három kör, hat induló, négy helyszín – válaszd ki a pályát.';
    head.append(kicker, title, sub);

    const card = make('div', 'track-card');

    const art = make('div', 'track-art track-art--canvas');
    art.setAttribute('aria-hidden', 'true');
    this.trackArtEl = document.createElement('canvas');
    this.trackArtEl.className = 'track-art-canvas';
    this.trackArtEl.width = 520;
    this.trackArtEl.height = 300;
    art.append(this.trackArtEl);

    const info = make('div', 'track-info');
    const infoKicker = make('span', 'kicker');
    infoKicker.textContent = 'PÁLYA';
    this.trackNameEl = make('h3', 'track-name');
    this.trackSubEl = make('p', 'track-sub');
    this.trackMetaEl = make('div', 'track-meta');
    info.append(infoKicker, this.trackNameEl, this.trackSubEl, this.trackMetaEl);

    const picksWrap = make('div', 'track-picks-wrap');
    const picksLabel = make('span', 'track-picks-label');
    picksLabel.textContent = 'RAJTOLÓK';
    this.trackPicksEl = make('div', 'track-picks');
    picksWrap.append(picksLabel, this.trackPicksEl);

    card.append(art, info, picksWrap);

    const chooser = make('div', 'track-choices');
    this.tracks.forEach((def) => {
      const btn = makeButton('track-choice');
      btn.setAttribute('aria-label', `${def.name} pálya`);
      const name = make('span', 'track-choice-name');
      name.textContent = def.name;
      const sub = make('span', 'track-choice-sub');
      sub.textContent = def.subtitle;
      btn.append(name, sub);
      btn.addEventListener('click', () => {
        this.trackId = def.id;
        this.renderTrack();
      });
      this.trackChoices.push(btn);
      chooser.append(btn);
    });

    const start = makeButton('start-btn', 'INDULHAT');
    start.setAttribute('aria-label', 'Verseny indítása');
    start.addEventListener('click', () => this.startRace());

    const setup = makeButton('ghost-btn track-setup-btn', 'VEZÉRLÉS BEÁLLÍTÁSA');
    setup.setAttribute('aria-label', 'Vezérlés beállítása');
    setup.addEventListener('click', () => this.openControlSetup());

    const options = make('div', 'mode-options');
    this.aiToggleEl = makeButton('mode-toggle', 'AI ELLENFELEK: BE');
    this.aiToggleEl.addEventListener('click', () => {
      if (this.mode === 1) return;
      this.noAi = !this.noAi;
      this.renderTrack();
    });
    this.battleToggleEl = makeButton('mode-toggle', 'BATTLE 2v2: KI');
    this.battleToggleEl.disabled = this.mobile;
    if (this.mobile) this.battleToggleEl.title = 'Mobilon csak egyjátékos mód érhető el';
    this.battleToggleEl.addEventListener('click', () => {
      if (this.mobile) return;
      this.battle = !this.battle;
      this.renderTrack();
    });
    this.nightToggleEl = makeButton('mode-toggle', 'ÉJSZAKA: KI');
    this.nightToggleEl.addEventListener('click', () => {
      this.night = !this.night;
      this.renderTrack();
    });
    options.append(this.aiToggleEl, this.battleToggleEl, this.nightToggleEl);
    const optionsHint = make('p', 'hint mode-options-hint');
    optionsHint.textContent = 'AI nélkül = PvP (csak emberek) · Battle 2v2 = csapatcsata életekkel';

    const hint = make('p', 'hint');
    hint.textContent = 'Enter = indulás · Esc = vissza a karakterekhez';

    section.append(head, card, chooser, setup, options, optionsHint, start, hint);
    return section;
  }

  private selectedTrack(): TrackDef | undefined {
    return this.tracks.find((t) => t.id === this.trackId) ?? this.tracks[0];
  }

  /** Kis pálya-előnézet a középvonalból. */
  private drawTrackPreview(def: TrackDef): void {
    const canvas = this.trackArtEl;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#0d121c';
    ctx.fillRect(0, 0, w, h);

    const pts = def.centerline;
    if (pts.length < 3) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const pad = 30;
    const scale = Math.min((w - pad * 2) / Math.max(1, maxX - minX), (h - pad * 2) / Math.max(1, maxY - minY));
    const ox = (w - (maxX - minX) * scale) / 2;
    const oy = (h - (maxY - minY) * scale) / 2;
    const map = (p: { x: number; y: number }): { x: number; y: number } => ({
      x: ox + (p.x - minX) * scale,
      y: oy + (p.y - minY) * scale,
    });

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    const first = map(pts[0]);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < pts.length; i++) {
      const q = map(pts[i]);
      ctx.lineTo(q.x, q.y);
    }
    ctx.closePath();
    ctx.strokeStyle = 'rgba(244, 241, 232, 0.16)';
    ctx.lineWidth = 11;
    ctx.stroke();
    ctx.strokeStyle = def.ambient.accent;
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // rajtvonal-pötty
    const startIdx = Math.floor(def.startS * pts.length) % pts.length;
    const sp = map(pts[startIdx]);
    ctx.fillStyle = '#ff5c4d';
    ctx.beginPath();
    ctx.arc(sp.x, sp.y, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Indulás előtt: ki mivel játszik (billentyűzet-kiosztás vagy gamepad). */
  private openControlSetup(): void {
    const players: { playerIndex: number; characterName: string }[] = [];
    for (let i = 0; i < this.mode; i++) {
      const character = this.characterById(this.picks[i]);
      players.push({ playerIndex: i, characterName: character ? character.short : `P${i + 1}` });
    }
    this.setupOpen = true;
    showControlSetup(this.root, {
      players,
      onDone: () => {
        this.setupOpen = false;
      },
    });
  }

  // ---------- állapot / render ----------

  private render(): void {
    this.el.dataset.stage = this.stage;
    this.backEl.hidden = this.stage === 'title';
    this.stepEl.textContent = STAGE_STEP[this.stage];

    if (this.stage === 'mode') this.renderMode();
    if (this.stage === 'pick') this.renderPick();
    if (this.stage === 'track') this.renderTrack();
  }

  private renderMode(): void {
    if (this.mode === 1) this.noAi = false;
    this.modeEls.forEach((card, index) => {
      const mode = MODES[index];
      card.classList.toggle('is-cursor', index === this.modeCursor);
      card.classList.toggle('is-active', mode === this.mode);
      card.setAttribute('aria-pressed', String(mode === this.mode));
    });
  }

  private renderPick(): void {
    this.pickBadge.textContent = `${this.pickerIndex + 1}. JÁTÉKOS`;
    this.pickTitle.textContent = 'VÁLASSZ KARAKTERT';
    this.pickSub.textContent =
      this.pickerIndex === 0
        ? 'Ki ül be először? A foglalt kártyákat a többiek már elvitték.'
        : 'A már kiválasztott karakterek foglaltak – válassz másikat.';

    this.pipsEl.replaceChildren();
    for (let i = 0; i < this.mode; i++) {
      const pip = make('span', 'pick-pip');
      pip.textContent = String(i + 1);
      if (i === this.pickerIndex) pip.classList.add('is-current');
      const picked = this.characterById(this.picks[i]);
      if (picked) {
        pip.classList.add('is-done');
        pip.title = `${i + 1}. játékos: ${picked.short}`;
      } else {
        pip.title = `${i + 1}. játékos`;
      }
      pip.setAttribute('aria-label', pip.title);
      this.pipsEl.append(pip);
    }

    this.cardRefs.forEach((refs, index) => {
      const character = this.characters[index];
      const taken = this.takenByOthers(character.id);
      const isPicked = this.picks[this.pickerIndex] === character.id;
      refs.card.disabled = taken;
      refs.card.classList.toggle('is-taken', taken);
      refs.card.classList.toggle('is-picked', isPicked);
      refs.card.classList.toggle('is-cursor', !this.onRandom && index === this.cardCursor);
      refs.taken.hidden = !taken;
      refs.picked.hidden = !isPicked;
    });

    this.randomEl.classList.toggle('is-cursor', this.onRandom);
  }

  private renderTrack(): void {
    const def = this.selectedTrack();
    if (!def) return;
    this.trackNameEl.textContent = def.name;
    this.trackSubEl.textContent = this.battle
      ? 'Csapatcsata · 2v2 · 3 élet'
      : this.noAi
        ? 'PvP · csak emberek'
        : def.subtitle;
    if (this.battle) {
      this.trackMetaEl.textContent = `BATTLE 2v2 · 3 ÉLET · ${this.mode} JÁTÉKOS`;
    } else if (this.noAi) {
      this.trackMetaEl.textContent = `${def.laps} KÖR · PvP (AI NÉLKÜL) · ${this.mode} JÁTÉKOS`;
    } else {
      this.trackMetaEl.textContent = `${def.laps} KÖR · 6 INDULÓ · ${this.mode} JÁTÉKOS`;
    }
    if (this.night) this.trackMetaEl.textContent += ' · ÉJSZAKA';

    this.trackChoices.forEach((btn, index) => {
      btn.classList.toggle('is-active', this.tracks[index]?.id === def.id);
    });
    this.drawTrackPreview(def);

    if (this.aiToggleEl) {
      this.aiToggleEl.textContent = `AI ELLENFELEK: ${this.noAi ? 'KI' : 'BE'}`;
      this.aiToggleEl.classList.toggle('is-active', !this.noAi);
      this.aiToggleEl.disabled = this.mode === 1;
      this.aiToggleEl.title = this.mode === 1 ? 'Egyedül nem kapcsolható ki az AI' : '';
    }
    if (this.battleToggleEl) {
      this.battleToggleEl.disabled = this.mobile;
      this.battleToggleEl.textContent = `BATTLE 2v2: ${this.battle ? 'BE' : 'KI'}`;
      this.battleToggleEl.classList.toggle('is-active', this.battle);
    }
    if (this.nightToggleEl) {
      this.nightToggleEl.textContent = `ÉJSZAKA: ${this.night ? 'BE' : 'KI'}`;
      this.nightToggleEl.classList.toggle('is-active', this.night);
    }

    this.trackPicksEl.replaceChildren();
    for (let i = 0; i < this.mode; i++) {
      const character = this.characterById(this.picks[i]);
      const chip = make('span', 'track-pick');
      if (character) chip.style.setProperty('--char', character.colors.body);
      const number = make('span', 'track-pick-num');
      number.textContent = String(i + 1);
      const name = make('span', 'track-pick-name');
      name.textContent = character ? character.short : '—';
      chip.append(number, name);
      this.trackPicksEl.append(chip);
    }
  }

  // ---------- navigáció ----------

  /** Beállítások a főképernyőről: hang és vezérlés. */
  private openSettings(): void {
    if (this.settingsOpen) return;
    this.settingsOpen = true;
    const players = [0, 1, 2, 3].map((i) => ({ playerIndex: i, characterName: '' }));
    showSettings(this.root, {
      sound: this.options.sound,
      players,
      onDone: () => {
        this.settingsOpen = false;
      },
    });
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.visible || this.onlineOpen) return;
    // a vezérlés-beállító / beállítások overlay saját maga kezeli a billentyűket
    if (this.setupOpen || this.settingsOpen) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const key = event.key;
    const confirm = key === 'Enter' || key === ' ' || key === 'Spacebar';
    if (this.stage === 'title' && (key === 's' || key === 'S')) {
      event.preventDefault();
      event.stopPropagation();
      this.openSettings();
      return;
    }
    const handled =
      confirm ||
      key === 'Escape' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown';
    if (!handled) return;

    event.preventDefault();
    event.stopPropagation();

    switch (this.stage) {
      case 'title':
        if (confirm) this.toMode();
        break;
      case 'mode':
        if (confirm) {
          if (!event.repeat) this.chooseMode();
        } else if (key === 'Escape') {
          if (!event.repeat) this.back();
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          this.moveMode(-1);
        } else if (key === 'ArrowRight' || key === 'ArrowDown') {
          this.moveMode(1);
        }
        break;
      case 'pick':
        if (confirm) {
          if (!event.repeat) this.confirmPick();
        } else if (key === 'Escape') {
          if (!event.repeat) this.back();
        } else if (key === 'ArrowLeft') {
          this.moveCard(-1);
        } else if (key === 'ArrowRight') {
          this.moveCard(1);
        } else if (key === 'ArrowDown' && !this.onRandom) {
          this.onRandom = true;
          this.renderPick();
        } else if (key === 'ArrowUp' && this.onRandom) {
          this.onRandom = false;
          this.renderPick();
        }
        break;
      case 'track':
        if (confirm) {
          if (!event.repeat) this.startRace();
        } else if (key === 'Escape') {
          if (!event.repeat) this.back();
        }
        break;
      default:
        break;
    }
  }

  private toMode(): void {
    this.practice = false;
    this.stage = 'mode';
    this.render();
  }

  /** Egyedül, AI nélkül, 3 kör – egyből a karakterválasztóra visz. */
  private startPractice(): void {
    this.practice = true;
    this.battle = false;
    this.night = false;
    this.mode = 1;
    this.modeCursor = 0;
    this.picks = [null];
    this.pickerIndex = 0;
    this.cardCursor = this.firstAvailable();
    this.onRandom = false;
    this.stage = 'pick';
    this.render();
  }

  /**
   * ONLINE: a lobby külön overlay, közben a menü nem kap billentyűt.
   * Sikeres indításnál a menü eredménye hordozza a meccs leírását.
   */
  private async openOnline(): Promise<void> {
    const opener = this.options.online;
    if (!opener || this.onlineOpen) return;
    this.onlineOpen = true;
    let match: OnlineMatchInfo | null = null;
    try {
      match = await opener.open();
    } finally {
      this.onlineOpen = false;
    }
    if (!match) {
      this.el.focus({ preventScroll: true });
      return;
    }
    const resolve = this.resolvePending;
    this.resolvePending = null;
    this.pending = null;
    this.hide();
    resolve?.({
      mode: match.humans.length >= 4 ? 4 : match.humans.length === 2 ? 2 : 1,
      picks: match.humans.map((human) => human.characterId),
      trackId: match.trackId,
      night: match.night,
      noAi: !match.ai,
      online: match,
    });
  }

  private chooseMode(): void {
    const mode = MODES.at(this.modeCursor);
    if (!mode) return;
    if (this.mobile && mode !== 1) return;
    this.mode = mode;
    this.picks = Array.from({ length: mode }, () => null);
    this.pickerIndex = 0;
    this.cardCursor = this.firstAvailable();
    this.onRandom = false;
    this.stage = 'pick';
    this.render();
  }

  private moveMode(direction: number): void {
    if (this.mobile) {
      this.modeCursor = 0;
      this.renderMode();
      return;
    }
    this.modeCursor = (this.modeCursor + direction + MODES.length) % MODES.length;
    this.renderMode();
  }

  private confirmPick(): void {
    if (this.onRandom) this.chooseRandom();
    else this.chooseCharacter(this.cardCursor);
  }

  private chooseCharacter(index: number): void {
    const character = this.characters[index];
    if (!character) return;
    if (this.takenByOthers(character.id)) {
      this.shake(index);
      return;
    }
    this.picks[this.pickerIndex] = character.id;
    this.advance();
  }

  private chooseRandom(): void {
    const pool = this.characters.filter((character) => !this.takenByOthers(character.id));
    if (pool.length === 0) return;
    const character = pool[Math.floor(Math.random() * pool.length)];
    if (!character) return;
    this.picks[this.pickerIndex] = character.id;
    this.advance();
  }

  private advance(): void {
    if (this.pickerIndex + 1 < this.mode) {
      this.pickerIndex++;
      this.cardCursor = this.firstAvailable();
      this.onRandom = false;
      this.stage = 'pick';
      this.render();
      return;
    }
    this.stage = 'track';
    this.render();
  }

  private back(): void {
    switch (this.stage) {
      case 'mode':
        this.stage = 'title';
        this.render();
        break;
      case 'pick':
        if (this.pickerIndex > 0) {
          this.pickerIndex--;
          this.cardCursor = this.firstAvailable();
          this.onRandom = false;
          this.stage = 'pick';
        } else if (this.practice) {
          this.practice = false;
          this.stage = 'title';
        } else {
          this.stage = 'mode';
          this.modeCursor = Math.max(0, MODES.indexOf(this.mode));
        }
        this.render();
        break;
      case 'track':
        this.pickerIndex = this.mode - 1;
        this.cardCursor = this.firstAvailable();
        this.onRandom = false;
        this.stage = 'pick';
        this.render();
        break;
      default:
        break;
    }
  }

  private moveCard(direction: number): void {
    const count = this.characters.length;
    if (count === 0) return;

    if (this.onRandom) {
      this.onRandom = false;
      this.cardCursor = direction < 0 ? this.lastAvailable() : this.firstAvailable();
      this.renderPick();
      return;
    }

    let index = this.cardCursor;
    for (let step = 0; step < count; step++) {
      index = (index + direction + count) % count;
      const candidate = this.characters[index];
      if (candidate && !this.takenByOthers(candidate.id)) {
        this.cardCursor = index;
        break;
      }
    }
    this.renderPick();
  }

  private takenByOthers(id: string): boolean {
    return this.picks.some((picked, index) => index !== this.pickerIndex && picked === id);
  }

  private firstAvailable(): number {
    const index = this.characters.findIndex((character) => !this.takenByOthers(character.id));
    return index >= 0 ? index : 0;
  }

  private lastAvailable(): number {
    for (let i = this.characters.length - 1; i >= 0; i--) {
      const character = this.characters[i];
      if (character && !this.takenByOthers(character.id)) return i;
    }
    return 0;
  }

  private characterById(id: string | null): CharacterDef | undefined {
    if (!id) return undefined;
    return this.characters.find((character) => character.id === id);
  }

  private shake(index: number): void {
    const refs = this.cardRefs[index];
    if (!refs) return;
    refs.card.classList.remove('is-shake');
    void refs.card.offsetWidth;
    refs.card.classList.add('is-shake');
  }

  private startRace(): void {
    const picks: string[] = [];
    for (let i = 0; i < this.mode; i++) {
      const id = this.picks[i];
      if (!id) return;
      picks.push(id);
    }

    const resolve = this.resolvePending;
    this.resolvePending = null;
    this.pending = null;
    this.hide();
    resolve?.({
      mode: this.mode,
      picks,
      trackId: this.trackId,
      practice: this.practice,
      battle: this.battle,
      night: this.night,
      noAi: this.mode === 1 ? false : this.noAi,
    });
  }
}

/** Menü létrehozása a megadott konténerbe (a #ui pointer-events: none, ezért a menü auto). */
export function createMenu(root: HTMLElement, options: MenuOptions): MenuController {
  return new MenuControllerImpl(root, options);
}

// ---------- eredménytábla ----------

const formatResultTime = (time: number | null): string => {
  if (time === null || !Number.isFinite(time) || time <= 0) return '–';
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(2)}`;
};

/** Eredménytábla; a visszaadott függvény eltakarítja az overlay-t. */
export function showResults(
  root: HTMLElement,
  result: RaceResult,
  handlers: {
    onRestart: () => void;
    onMenu: () => void;
    restartLabel?: string;
    menuLabel?: string;
    hint?: string;
  },
): () => void {
  const entries: RaceResultEntry[] = [...result.entries].sort((a, b) => a.place - b.place);
  const winner = entries.at(0) ?? null;

  const el = make('div', 'menu menu-results');
  el.tabIndex = -1;
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');

  const panel = make('div', 'menu-panel results-panel');
  const titleId = `results-title-${Date.now().toString(36)}`;
  el.setAttribute('aria-labelledby', titleId);

  const kicker = make('span', 'kicker');
  kicker.textContent = result.online
    ? `ONLINE · ${result.trackName}`
    : `${result.trackName} · ${result.mode} JÁTÉKOS`;
  const title = make('h2', 'results-title');
  title.id = titleId;
  title.textContent = 'VÉGEREDMÉNY';

  const winnerBox = make('div', 'winner');
  const crown = make('span', 'winner-crown');
  crown.textContent = '★';
  const winnerName = make('h3', 'winner-name');
  winnerName.textContent = winner ? winner.name : 'Nincs célba érő';
  const winnerLabel = make('span', 'winner-label');
  winnerLabel.textContent = 'GYŐZTES';
  winnerBox.append(crown, winnerName, winnerLabel);

  const table = make('table', 'result-table');
  const thead = make('thead');
  const headRow = make('tr');
  for (const label of ['HELY', 'NÉV', 'ÖSSZIDŐ', 'LEGJOBB KÖR']) {
    const th = make('th');
    th.scope = 'col';
    th.textContent = label;
    headRow.append(th);
  }
  thead.append(headRow);

  const tbody = make('tbody');
  entries.forEach((entry) => {
    const row = make('tr');
    if (entry.isPlayer) row.classList.add('is-player');
    if (winner && entry === winner) row.classList.add('is-winner');

    const place = make('td', 'result-place');
    place.textContent = `${entry.place}.`;

    const nameCell = make('td');
    const nameWrap = make('span', 'result-name');
    const dot = make('span', 'result-dot');
    dot.style.background = entry.color;
    const name = make('span', 'result-name-text');
    name.textContent = entry.name;
    nameWrap.append(dot, name);
    if (entry.isPlayer) {
      const badge = make('span', 'you-badge');
      badge.textContent = 'EMBER';
      nameWrap.append(badge);
    }
    nameCell.append(nameWrap);

    const total = make('td', 'result-time');
    total.textContent = entry.finished ? formatResultTime(entry.time) : '–';

    const best = make('td', 'result-time');
    best.textContent = formatResultTime(entry.bestLap);

    row.append(place, nameCell, total, best);
    tbody.append(row);
  });

  table.append(thead, tbody);

  const actions = make('div', 'results-actions');
  const restartBtn = makeButton('start-btn results-start', handlers.restartLabel ?? 'ÚJRA');
  const menuBtn = makeButton('ghost-btn', handlers.menuLabel ?? 'MENÜ');
  actions.append(restartBtn, menuBtn);

  const hint = make('p', 'hint');
  hint.textContent = handlers.hint ?? 'Enter = újra · Esc = menü';

  panel.append(kicker, title, winnerBox, table, actions, hint);
  el.append(panel);

  let cleaned = false;

  function cleanup(): void {
    if (cleaned) return;
    cleaned = true;
    window.removeEventListener('keydown', onKey, true);
    el.remove();
  }

  function restart(): void {
    cleanup();
    handlers.onRestart();
  }

  function goMenu(): void {
    cleanup();
    handlers.onMenu();
  }

  function onKey(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) restart();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) goMenu();
    }
  }

  restartBtn.addEventListener('click', restart);
  menuBtn.addEventListener('click', goMenu);
  window.addEventListener('keydown', onKey, true);

  root.appendChild(el);
  el.focus({ preventScroll: true });

  return cleanup;
}
